/**
 * Yutrients shared 3-step submit loader (founder spec 2026-08-13,
 * choreography rework same day).
 *
 * One component used by BOTH builders' submit legs:
 *   - BYO (yt-byo.js): in-place inside the yt-byo-loading section.
 *   - Guided chat (yt-chat.js): full-viewport overlay on the chat page.
 *
 * Layout (top → bottom): current step TITLE, three "Step N" chips that
 * highlight as the flow advances, ONE bar that refills per step. Steps:
 *   1 Formulating your blend  ·  2 Checking your blend  ·  3 Making your blend
 *
 * TIMELINE MODE (the shipped behaviour — founder call: "we don't need
 * it smart, we need it to work for everyone"): the bars run a FIXED,
 * designed schedule regardless of how fast the backend happens to be,
 * so every customer sees the same paced sequence. Steps 1 and 2 fill
 * 0→100 over their scripted duration; step 3 fills to `holdAt` (90%)
 * and WAITS there — the one reality gate. The host calls finish() when
 * the real work is verified done, which glides 90→100 (~1.1s) before
 * the redirect. If the backend runs longer than the script, the bar
 * simply holds at 90 until it's truly ready; if it's faster, the
 * choreography still plays out in full.
 *
 * The event-driven mode (start()/setStep()) remains for compatibility
 * but no host uses it since the rework.
 *
 * Reduced motion: static 88% bars, steps advance on schedule, no glides.
 */
(function () {
  'use strict';

  var STEP_LABELS = [
    'Reviewing your inputs',
    'Building your formula',
    'Running final checks',
  ];
  var GLIDE_TARGET = 93; // event-mode crawl target (legacy path)
  var COMPLETE_HOLD_MS = 320; // beat at 100% before the next bar restarts
  var FINISH_GLIDE_MS = 1100; // the "slowly goes to the end" release

  function YtSubmitSteps(container, opts) {
    opts = opts || {};
    this.container = container;
    /** Event-mode glide paces (legacy; unused in timeline mode). */
    this.paces = opts.paces || [8000, 26000, 9000];
    this.minDwellMs = typeof opts.minDwellMs === 'number' ? opts.minDwellMs : 1400;
    /** Optional external element that shows the current step title. */
    this.titleEl = opts.titleEl || null;
    this.reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.current = -1;
    this._timeline = false;
    this._holdTimer = null;
    this._dwellTimer = null;
    this._crawlTimer = null;
    this._timelineTimers = [];
    this._stepStartedAt = 0;
    this._pendingStep = -1;
    this._build(opts.ariaLabel || 'Creating your formula');
  }

  YtSubmitSteps.prototype._build = function (ariaLabel) {
    var root = document.createElement('div');
    root.className = 'yt-steps';

    if (!this.titleEl) {
      this.titleEl = document.createElement('p');
      this.titleEl.className = 'yt-steps__title yt-display';
      root.appendChild(this.titleEl);
    }
    this.titleEl.setAttribute('aria-live', 'polite');

    this.chips = [];
    var chipRow = document.createElement('div');
    chipRow.className = 'yt-steps__chips';
    for (var i = 0; i < STEP_LABELS.length; i++) {
      var chip = document.createElement('span');
      chip.className = 'yt-steps__chip';
      chip.textContent = 'Step ' + (i + 1);
      chipRow.appendChild(chip);
      this.chips.push(chip);
    }
    root.appendChild(chipRow);

    this.track = document.createElement('div');
    this.track.className = 'yt-steps__track';
    this.track.setAttribute('role', 'progressbar');
    this.track.setAttribute('aria-label', ariaLabel);
    this.track.setAttribute('aria-valuemin', '0');
    this.track.setAttribute('aria-valuemax', '100');
    this.track.setAttribute('aria-valuenow', '0');
    this.bar = document.createElement('div');
    this.bar.className = 'yt-steps__bar';
    this.track.appendChild(this.bar);
    root.appendChild(this.track);

    this.container.textContent = '';
    this.container.appendChild(root);
  };

  /* ── Timeline mode (the shipped choreography) ─────────────────── */

  /**
   * Run the fixed choreography. durations = [step1Ms, step2Ms, step3Ms]
   * where step 3's duration is its 0→holdAt fill; it then waits for
   * finish(). The schedule is wall-clock — backend speed changes
   * NOTHING except how long the bar rests at holdAt.
   */
  YtSubmitSteps.prototype.startTimeline = function (opts) {
    opts = opts || {};
    var durations = opts.durations || [12000, 8000, 8000];
    // The final step fills to holdAt on schedule, then keeps CREEPING
    // in TWO stages (founder 2026-08-13 + build-time data, 673
    // sessions: the remaining wait at 75% is ~5s median / ~13s p75 /
    // ~24s p90): stage 1 at the founder's 1.5%/s (75→90 over 10s)
    // covers the typical run at visible speed; stage 2 slows to
    // 0.25%/s (90→96 over 24s) so the p90 tail still never parks.
    // finish() overrides either stage. No dead stop, ever.
    var holdAt = typeof opts.holdAt === 'number' ? opts.holdAt : 75;
    var crawlStages = opts.crawlStages || [[90, 10000], [96, 24000]];
    var self = this;
    this._clearTimers();
    this._timeline = true;
    this.current = -1;

    var at = 0;
    for (var i = 0; i < durations.length; i++) {
      (function (idx, startAt) {
        self._timelineTimers.push(
          setTimeout(function () {
            self._activate(idx);
            self._resetBar();
            var last = idx === durations.length - 1;
            self._fill(last ? holdAt : 100, durations[idx]);
            if (last && !self.reduceMotion) {
              // When the scripted fill lands at holdAt, hand over to
              // the staged waiting crawl.
              var stageAt = durations[idx];
              for (var sIdx = 0; sIdx < crawlStages.length; sIdx++) {
                (function (target, ms, at) {
                  self._timelineTimers.push(
                    setTimeout(function () {
                      self.bar.style.transition = 'width ' + ms + 'ms linear';
                      self.bar.style.width = target + '%';
                      self.track.setAttribute('aria-valuenow', String(target));
                    }, at),
                  );
                })(crawlStages[sIdx][0], crawlStages[sIdx][1], stageAt);
                stageAt += crawlStages[sIdx][1];
              }
            }
          }, startAt),
        );
      })(i, at);
      at += durations[i] + COMPLETE_HOLD_MS;
    }
  };

  /** Chip/title bookkeeping shared by both modes. */
  YtSubmitSteps.prototype._activate = function (idx) {
    for (var i = 0; i < this.chips.length; i++) {
      this.chips[i].classList.toggle('yt-steps__chip--done', i < idx);
      this.chips[i].classList.toggle('yt-steps__chip--active', i === idx);
    }
    if (this.titleEl) this.titleEl.textContent = STEP_LABELS[idx] + '…';
    this.current = idx;
    this._stepStartedAt = Date.now();
  };

  /** Designed fill: smooth ease-in-out to `pct` over `ms`. */
  YtSubmitSteps.prototype._fill = function (pct, ms) {
    if (this.reduceMotion) {
      this.bar.style.transition = 'none';
      this.bar.style.width = Math.min(pct, 88) + '%';
      this.track.setAttribute('aria-valuenow', String(Math.min(pct, 88)));
      return;
    }
    this.bar.style.transition = 'width ' + ms + 'ms cubic-bezier(0.45, 0.05, 0.35, 1)';
    this.bar.style.width = pct + '%';
    this.track.setAttribute('aria-valuenow', String(pct));
  };

  /* ── Event mode (legacy — kept for compatibility) ─────────────── */

  YtSubmitSteps.prototype.start = function () {
    this._clearTimers();
    this._timeline = false;
    this.current = -1;
    this.setStep(0);
  };

  YtSubmitSteps.prototype.setStep = function (idx) {
    if (this._timeline) return; // timeline owns transitions
    if (idx <= this.current && this._pendingStep < 0) return;
    var self = this;

    var elapsed = Date.now() - this._stepStartedAt;
    if (this.current >= 0 && !this.reduceMotion && elapsed < this.minDwellMs) {
      this._pendingStep = Math.max(this._pendingStep, idx);
      if (!this._dwellTimer) {
        this._dwellTimer = setTimeout(function () {
          self._dwellTimer = null;
          var target = self._pendingStep;
          self._pendingStep = -1;
          if (target > self.current) self.setStep(target);
        }, this.minDwellMs - elapsed);
      }
      return;
    }
    if (idx <= this.current) return;
    clearTimeout(this._holdTimer);
    clearTimeout(this._crawlTimer);

    var beginGlide = function () {
      self._resetBar();
      self._glide(self.paces[idx] || 9000);
    };

    var hadPrevious = this.current >= 0;
    this._activate(idx);
    if (hadPrevious && !this.reduceMotion) {
      this._snapTo(100);
      this._holdTimer = setTimeout(beginGlide, COMPLETE_HOLD_MS);
    } else {
      beginGlide();
    }
  };

  YtSubmitSteps.prototype._glide = function (durationMs) {
    if (this.reduceMotion) {
      this.bar.style.transition = 'none';
      this.bar.style.width = '88%';
      this.track.setAttribute('aria-valuenow', '88');
      return;
    }
    this.bar.style.transition =
      'width ' + durationMs + 'ms cubic-bezier(0.16, 0.84, 0.44, 1)';
    this.bar.style.width = GLIDE_TARGET + '%';
    this.track.setAttribute('aria-valuenow', String(GLIDE_TARGET));
    var self = this;
    clearTimeout(this._crawlTimer);
    this._crawlTimer = setTimeout(function () {
      self.bar.style.transition = 'width 45000ms linear';
      self.bar.style.width = '98%';
    }, durationMs);
  };

  /* ── Terminal states ──────────────────────────────────────────── */

  /**
   * Terminal success. In timeline mode this is the reality gate
   * release: the bar glides from wherever it holds to 100 over ~1.1s
   * — hosts should wait ~FINISH_GLIDE_MS + a beat before navigating
   * so the customer SEES the completion.
   */
  YtSubmitSteps.prototype.finish = function (message) {
    this._clearTimers();
    for (var i = 0; i < this.chips.length; i++) {
      this.chips[i].classList.add('yt-steps__chip--done');
      this.chips[i].classList.remove('yt-steps__chip--active');
    }
    if (message && this.titleEl) this.titleEl.textContent = message;
    if (this.reduceMotion) {
      this.bar.style.transition = 'none';
      this.bar.style.width = '100%';
      this.track.setAttribute('aria-valuenow', '100');
      return;
    }
    this.bar.style.transition = 'width ' + FINISH_GLIDE_MS + 'ms cubic-bezier(0.22, 0.6, 0.36, 1)';
    this.bar.style.width = '100%';
    this.track.setAttribute('aria-valuenow', '100');
  };

  /** Terminal failure: freeze where we are (host swaps to its error UI). */
  YtSubmitSteps.prototype.fail = function () {
    this._clearTimers();
    var w = parseFloat(this.bar.style.width) || 0;
    this._snapTo(w);
  };

  /* ── Internals ────────────────────────────────────────────────── */

  YtSubmitSteps.prototype._clearTimers = function () {
    clearTimeout(this._holdTimer);
    clearTimeout(this._dwellTimer);
    clearTimeout(this._crawlTimer);
    for (var i = 0; i < this._timelineTimers.length; i++) {
      clearTimeout(this._timelineTimers[i]);
    }
    this._timelineTimers = [];
    this._dwellTimer = null;
    this._pendingStep = -1;
  };

  YtSubmitSteps.prototype._resetBar = function () {
    this.bar.style.transition = 'none';
    this.bar.style.width = '0%';
    void this.bar.offsetWidth; // commit the 0% before any fill starts
    this.track.setAttribute('aria-valuenow', '0');
  };

  YtSubmitSteps.prototype._snapTo = function (pct) {
    this.bar.style.transition = 'width 180ms ease-out';
    this.bar.style.width = pct + '%';
    this.track.setAttribute('aria-valuenow', String(Math.round(pct)));
  };

  /**
   * Poll the storefront product JSON until `variantId` is present —
   * i.e. the just-minted variant is actually buyable. In the timeline
   * design this feeds the host's "really done" signal that releases
   * step 3 from its hold.
   */
  function ytPollVariantReady(productHandle, variantId, opts) {
    opts = opts || {};
    var deadline = Date.now() + (opts.timeoutMs || 12000);
    var gapMs = opts.gapMs || 2000;
    var wanted = String(variantId);
    return new Promise(function (resolve) {
      function attempt() {
        fetch('/products/' + productHandle + '.js?ytv=' + Date.now(), {
          headers: { Accept: 'application/json' },
        })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (json) {
            var variants = (json && json.variants) || [];
            for (var i = 0; i < variants.length; i++) {
              if (String(variants[i].id) === wanted) return resolve(true);
            }
            if (Date.now() >= deadline) return resolve(false);
            setTimeout(attempt, gapMs);
          })
          .catch(function () {
            if (Date.now() >= deadline) return resolve(false);
            setTimeout(attempt, gapMs);
          });
      }
      attempt();
    });
  }

  window.YtSubmitSteps = YtSubmitSteps;
  window.ytPollVariantReady = ytPollVariantReady;
})();
